"use client"

import { useSearchParams } from "next/navigation"
import { Suspense, useEffect } from "react"

function Listener({
  onChange,
}: {
  onChange: (params: URLSearchParams) => void
}) {
  const searchParams = useSearchParams()

  useEffect(() => {
    onChange(new URLSearchParams(searchParams.toString()))
  }, [searchParams, onChange])

  return null
}

/**
 * Reports the current URL search params to a parent, without dragging the
 * parent out of static rendering.
 *
 * `useSearchParams()` cannot be prerendered: on a statically rendered route it
 * throws "Bail out to client-side rendering: useSearchParams()" unless it sits
 * inside a Suspense boundary. Calling it directly in a big block component is
 * therefore a trap — the route either fails to prerender, or (once wrapped)
 * prerenders the FALLBACK, which drops the whole block out of the HTML and
 * costs the page its SEO content.
 *
 * Keeping the hook in a leaf that renders `null` means only this component
 * suspends. The parent keeps its server-rendered markup, and the params arrive
 * on mount via `onChange`.
 *
 * Pass a stable `onChange` (a `useState` setter, or a `useCallback`), since it
 * is an effect dependency.
 */
export default function SearchParamsListener({
  onChange,
}: {
  onChange: (params: URLSearchParams) => void
}) {
  return (
    <Suspense fallback={null}>
      <Listener onChange={onChange} />
    </Suspense>
  )
}
